import { Box } from "@mui/material";
import React from "react";

import VirtualizedList from "@/components/VirtualizedList";

/**
 * Above this many rows the list is virtualized; below it we render plain rows so small groups keep
 * their natural height and don't gain an inner scroll area.
 */
const DEFAULT_VIRTUALIZE_THRESHOLD = 40;
const DEFAULT_MAX_HEIGHT_PX = 320;

/**
 * Renders a selectable list of rows, virtualizing once it grows past a threshold so a single group
 * with thousands of channels (the WEB-2128 "Retention Large Single Group" workspace) mounts only the
 * visible rows instead of freezing the tab while every row is styled and laid out. Small lists render
 * plainly to preserve their natural height. Past the threshold the row count always exceeds the height
 * budget, so the virtualized container is simply capped at `maxHeightPx`.
 */
export default function VirtualizedSelectList<T extends { id: string }>({
  id,
  items,
  renderRow,
  itemSize,
  threshold = DEFAULT_VIRTUALIZE_THRESHOLD,
  maxHeightPx = DEFAULT_MAX_HEIGHT_PX,
}: {
  id: string;
  items: T[];
  renderRow: (item: T) => React.ReactNode;
  itemSize: number;
  threshold?: number;
  maxHeightPx?: number;
}) {
  if (!items?.length) {
    return null;
  }

  if (items.length <= threshold) {
    return (
      <>
        {items.map((item) => (
          <React.Fragment key={item.id}>{renderRow(item)}</React.Fragment>
        ))}
      </>
    );
  }

  return (
    <Box sx={{ height: maxHeightPx, width: "100%" }}>
      <VirtualizedList id={id} data={items} itemSize={itemSize} childComponent={(item: T) => renderRow(item)} />
    </Box>
  );
}
